import { useEffect } from 'react';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import KanbanShowcase from './components/KanbanShowcase';
import SchedulingShowcase from './components/SchedulingShowcase';
import BroadcastShowcase from './components/BroadcastShowcase';
import Features from './components/Features';
import SocialProof from './components/SocialProof';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goautomat.com';

export default function Home() {
  useEffect(() => {
    document.title = 'Automat | Sistema de IA para WhatsApp';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'O primeiro Sistema Operacional de IA All-in-One para WhatsApp. Automatize atendimento, vendas, agendamento e CRM. Clone seu melhor vendedor e escale sua receita sem contratar mais funcionários. ROI de 380% no primeiro ano.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'automação whatsapp, IA atendimento, chatbot inteligente, CRM whatsapp, vendas automatizadas, agendamento automático, voice agent, AI agent');
    }
  }, []);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Automat",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "Sistema de IA para automação de atendimento e vendas via WhatsApp",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+55-11-3000-0000",
      "contactType": "customer service",
      "availableLanguage": ["Portuguese"]
    },
    "sameAs": []
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Automat",
    "url": siteUrl,
    "description": "O primeiro Sistema Operacional de IA All-in-One para WhatsApp",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${siteUrl}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Automat",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "description": "O primeiro Sistema Operacional de IA All-in-One para WhatsApp. Automatize atendimento, vendas, agendamento e CRM. Clone seu melhor vendedor e escale sua receita sem contratar mais funcionários. ROI de 380% no primeiro ano.",
    "offers": [
      {
        "@type": "Offer",
        "name": "Automat PRO - Mensal",
        "price": "597",
        "priceCurrency": "BRL",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "name": "Automat PRO - Anual",
        "price": "477",
        "priceCurrency": "BRL",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "name": "Automat TEAMS - Mensal",
        "price": "997",
        "priceCurrency": "BRL",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        "name": "Automat TEAMS - Anual",
        "price": "797",
        "priceCurrency": "BRL",
        "priceValidUntil": "2025-12-31",
        "availability": "https://schema.org/InStock"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "847"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "O que é o Automat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Automat é o primeiro Sistema Operacional de IA All-in-One para WhatsApp. Ele automatiza atendimento, vendas, agendamento e CRM, permitindo que você clone seu melhor vendedor e escale sua receita sem contratar mais funcionários."
        }
      },
      {
        "@type": "Question",
        "name": "Quanto custa o Automat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O Automat oferece dois planos: PRO por R$ 597/mês (ou R$ 477/mês no plano anual com 20% de desconto) e TEAMS por R$ 997/mês (ou R$ 797/mês no plano anual com 20% de desconto). Os planos mensais incluem 7 dias de teste grátis."
        }
      },
      {
        "@type": "Question",
        "name": "O Automat funciona 24 horas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sim! O Automat está disponível 24 horas por dia, 7 dias por semana, atendendo seus clientes automaticamente mesmo quando você não está disponível."
        }
      },
      {
        "@type": "Question",
        "name": "Qual o ROI do Automat?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "O Automat oferece um ROI de 380% no primeiro ano, automatizando processos que antes exigiam múltiplos funcionários e aumentando significativamente a taxa de conversão de vendas."
        }
      }
    ]
  };

  const combinedSchema = [organizationSchema, websiteSchema, softwareSchema, faqSchema];

  return (
    <div className="bg-gray-950 min-h-screen">
      <SEOSchema schema={combinedSchema} />
      <Hero />
      <Problem />
      <Solution />
      <KanbanShowcase />
      <SchedulingShowcase />
      <BroadcastShowcase />
      <Features />
      <SocialProof />
      <FinalCTA />
      <Footer />
    </div>
  );
}
